import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft } from "lucide-react";
import { API, useAuth } from "@/App";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      });

      const raw = await response.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }

      if (!response.ok) {
        throw new Error(
          (typeof data.detail === "string" && data.detail) || "Registration failed"
        );
      }

      login(data);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      {/* Background */}
      <div className="absolute inset-0 bg-islamic-blue-950">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1676607185077-f6f289c669e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxpc2xhbWljJTIwZ2VvbWV0cmljJTIwcGF0dGVybiUyMGdvbGQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Njg1MDYyOTN8MA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-slate-400 hover:text-gold-400 mb-8 font-outfit transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        {/* Card */}
        <div className="glass rounded-2xl p-8 gold-glow">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/hifz-logo.png"
              alt="Hifz logo"
              className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg"
              data-testid="app-logo"
            />
            <h1 className="font-cinzel text-2xl font-bold text-slate-100">Begin Your Journey</h1>
            <p className="text-slate-400 font-outfit mt-2">Create your memorization account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-300 font-outfit">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="name-input"
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-300 font-outfit">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="email-input"
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300 font-outfit">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="password-input"
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300 font-outfit">Confirm Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="confirm-password-input"
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full btn-gold rounded-full py-6 font-outfit font-medium"
            >
              {isLoading ? (
                <div className="spinner w-5 h-5"></div>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center mt-6 text-slate-400 font-outfit">
            Already have an account?{" "}
            <Link to="/login" className="text-gold-400 hover:text-gold-300 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
